'use client';

import { notFound, useParams } from 'next/navigation';

import { ComponentPropsWithoutRef, MouseEvent, MouseEventHandler, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

import './Story.scss';
import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import Cookies from 'js-cookie';
import { kebabCase } from 'lodash';

import ImageProxy from '@next-image-proxy';

import {
  Collapse,
  Modal,
  ModalCloseButton,
  ModalContent,
  Spinner,
  useModalController,
} from '@uikit';

import { InfoCircle } from '@uikit/icons/InfoCircle';

import { ACTION_TYPES, CommonStory, DeserializedZealyTask } from '@shared/api/types/story';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { namedErrors } from '@shared/constants';
import { hexToRgb } from '@shared/helpers/color';
import { getErrorMessage } from '@shared/helpers/error';
import generateUrl from '@shared/helpers/imageProxy';
import useUserWalletStatusFlags from '@shared/helpers/web3/hooks/useUserWalletStatusFlags';
import {
  useSelfZealyAccountQuery,
  useStoryQuery,
  useVerifyZealyMutation,
  useZealyQuestsQuery,
} from '@shared/hooks/query/story';

import StoryBlocksItem from '@store:web/components/pages/StoryPage/StoryBlock';
import StoryHeadBanner from '@store:web/components/pages/StoryPage/StoryHeadBanner';
import StoryMainInfoBlock from '@store:web/components/pages/StoryPage/StoryMainInfoBlock';

import hardcodedStories from './hardcodedStories';
import { zealyLeaderboard } from './hardcodedStories/zealyLeaderboard';
import SoQuestWidgetQuest from './SoQuestWidgetQuest';
import StormTradeStory from './SpecificStory/StormTradeStory/StormTradeStory';
import ZealyLeaderboardStory, {
  ZealyInfiniteLeaderboard,
} from './SpecificStory/ZealyLeaderboardStory';

const safeParse = (patient?: string) => {
  try {
    return [...(JSON.parse(patient || '') || [])];
  } catch (error) {
    return [];
  }
};

const SoquestStory = ({ story }: { story: CommonStory }) => {
  const { config } = story?.content ?? {};

  return (
    <main className="store-container">
      <div className="relative mx-auto max-w-[1440px]">
        <Spinner className="absolute left-0 right-0 mx-auto top-16" size="lg" />

        {config && <SoQuestWidgetQuest config={config} />}
      </div>
    </main>
  );
};

const Leaderboard = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => {
  return (
    <div className={classNames('rounded-sm pt-6 flex flex-col', className)} {...props}>
      <div className="mb-4 gap-4 flex justify-between px-6">
        <span className="t-h3">Leaderboard</span>

        <NextLink href={`/stories/${zealyLeaderboard.id}`} className="text-secondary">
          See all ››
        </NextLink>
      </div>

      <ZealyInfiniteLeaderboard className="px-6 pb-6 flex flex-1 flex-col" />
    </div>
  );
};

const ZealyStory = () => {
  const { isAuth, user } = useAuth();
  const { data } = useZealyQuestsQuery();
  const { tagManager } = useTagManager();
  const { data: selfZealyAccount, isPending: isSelfZealyAccountLoading } = useSelfZealyAccountQuery(
    {
      enabled: isAuth,
    },
  );
  const { isEMVWalletConnected } = useUserWalletStatusFlags();
  const completeMagicIdModal = useCompleteMagicIdModal();
  const authModal = useAuthModal();
  const connectZealyAccountModal = useModalController();
  const { mutateAsync: verifyZealy, isPending: isVerifyingZealy } = useVerifyZealyMutation();
  const questsByCategory = useMemo(() => {
    const groupedQuestsByCategory = data?.quests.reduce((acc, quest) => {
      const { categoryId } = quest.attributes;

      if (!(categoryId in acc)) {
        acc[categoryId] = [];
      }

      acc[categoryId].push(quest);

      return acc;
    }, {} as Record<string, DeserializedZealyTask[]>);

    return groupedQuestsByCategory || {};
  }, [data]);

  const handleVerifyZealyButtonClick = async () => {
    if (!isEMVWalletConnected) {
      completeMagicIdModal.open({
        description: {
          wallets: {
            EVM: null,
          },
        },
        success: false,
      });

      return;
    }

    try {
      await verifyZealy();
    } catch (error) {
      toast.error(getErrorMessage(error, namedErrors.DEFAULT_RETRY));
    }
  };

  const handleQuestLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isAuth) {
      authModal.open();

      event.preventDefault();

      return;
    }

    if (!isEMVWalletConnected) {
      completeMagicIdModal.open({
        description: {
          wallets: {
            EVM: null,
          },
        },
        success: false,
      });

      event.preventDefault();

      return;
    }

    if (!selfZealyAccount) {
      connectZealyAccountModal.open();

      event.preventDefault();
    }
  };

  const handleGoToZealyLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isAuth) {
      authModal.open();

      event.preventDefault();

      return;
    }

    if (!isEMVWalletConnected) {
      completeMagicIdModal.open({
        description: {
          wallets: {
            EVM: null,
          },
        },
        success: false,
      });

      event.preventDefault();
    }
  };

  useEffect(() => {
    const activeCampaigns: any[] = safeParse(Cookies.get('activeCampaigns'));
    const readyCampaigns: any[] = safeParse(Cookies.get('readyCampaign'));

    if (!!activeCampaigns.length && user && selfZealyAccount) {
      activeCampaigns.forEach((campaign) => {
        const isReadyCampaign = readyCampaigns.includes(campaign);

        if (!isReadyCampaign) {
          tagManager.dataLayer({
            dataLayer: {
              event: 'gtm.createZealyAccountFromCampaign',
              url: window.location.href,
              userId: user.id,
              zealyId: selfZealyAccount.account.attributes.zealyId,
              campaign,
            },
          });
          Cookies.set(
            'readyCampaign',
            JSON.stringify([...safeParse(Cookies.get('readyCampaign')), campaign]),
            { expires: 365 },
          );
        }
      });
      Cookies.remove('activeCampaigns');
    }
  }, [selfZealyAccount, user, tagManager]);

  return (
    <>
      <main className="store-container">
        <ImageProxy
          alt="Road to TGE Banner"
          src="/stories/road-to-tge-banner.png"
          className="w-full h-auto mb-5 max-sm:hidden 3xl:mb-10"
          sizes="100vw"
          width={1742}
          height={296}
          priority
        />
        <ImageProxy
          alt="Road to TGE Banner"
          src="/stories/road-to-tge-banner-mobile.png"
          className="w-full h-auto mb-5 sm:hidden"
          sizes="100vw"
          width={278}
          height={106}
          priority
        />

        <div className="mb-6 sm:mb-8 md:mb-5 xl:mb-6 2xl:mb-9">
          <h1 className="font-semibold text-lg text-center md:text-3xl">
            Complete Daily Tasks & Earn with Magic Square: $2.5M $SQR CoinList Campaign
          </h1>

          <Collapse isOpen={isAuth && !isSelfZealyAccountLoading && !selfZealyAccount}>
            <div
              className={classNames(
                'rounded-2xl gap-4 flex-col mt-5 py-2 px-4 border flex items-center bg-red-200 border-red-600 sm:mt-10 sm:p-8 sm:flex-row 3xl:mt-5',
              )}
            >
              <InfoCircle className="icon text-red-600" />

              <span className="font-medium max-sm:text-center xl:text-2xl flex-1">
                In order to participate your wallet must be connect to Zealy
              </span>
              <div className="flex flex-col gap-4 max-sm:w-full lg:flex-row">
                <a
                  href={data?.campaign?.attributes.invite}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleGoToZealyLinkClick}
                  className="button button-outline-secondary button-md whitespace-nowrap"
                >
                  Go To Zealy
                </a>
                <button
                  className={classNames('button button-solid-secondary button-md min-w-[6.25rem]', {
                    'pointer-events-none': isVerifyingZealy,
                  })}
                  onClick={handleVerifyZealyButtonClick}
                >
                  {isVerifyingZealy ? <Spinner size="sm" color="light" /> : 'Verify'}
                </button>
              </div>
            </div>
          </Collapse>
        </div>

        <ul className="flex flex-wrap gap-1 mt-5 mb-7 sm:mb-9 sm:gap-4 md:mb-11">
          {data?.categories.map((category) => {
            const categoryColor = hexToRgb(category.attributes.color || '#4056F7');

            return (
              <li
                key={category.id}
                style={{
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  '--rgb': categoryColor.join(' '),
                }}
              >
                <a
                  href={`#${kebabCase(category.attributes.name)}`}
                  className="text-[rgb(var(--rgb))] border rounded-full border-primary-300 shadow-main px-4 py-1 text-lg font-semibold flex justify-center items-center"
                >
                  {category.attributes.name}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-col md:flex-1 gap-7">
          {data?.categories.map((category, i) => {
            const categoryColor = hexToRgb(category.attributes.color || '#4056F7');
            const tagClassName =
              'text-xs flex justify-center items-center rounded-[0.25rem] min-h-[1.5rem] px-1.5 shrink-0';

            return (
              <section
                id={`${kebabCase(category.attributes.name)}`}
                style={{
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  '--rgb': categoryColor.join(' '),
                }}
                key={category.id}
              >
                <header className="mb-7">
                  <h2 className={classNames('font-semibold text-lg text-[rgb(var(--rgb))]')}>
                    #{category.attributes.name}
                  </h2>
                </header>
                <ul
                  className="grid auto-rows-[14.375rem] grid-cols-[repeat(auto-fill,_minmax(min(100%,_424px),_1fr))] gap-4"
                  key={category.id}
                >
                  {questsByCategory[category.id]?.map((quest) => {
                    const selfQuestInfo = selfZealyAccount?.quests?.[quest.id];
                    const xp = selfQuestInfo?.attributes.xp || quest.attributes.xp;
                    const count = selfQuestInfo?.attributes.count || quest.attributes.count;
                    const frequency = quest.attributes.frequency;
                    const totalXp = xp * count;
                    const isOneTime = frequency === 'ONE-TIME';
                    const isCompleted = isOneTime && !!selfQuestInfo?.attributes.completed;

                    return (
                      <li key={quest.id}>
                        <a
                          href={quest.attributes.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={handleQuestLinkClick}
                          className={classNames(
                            'flex flex-col w-full h-full rounded-sm p-5 transition-all',
                            'bg-[rgb(var(--rgb))]/5 hover:bg-[rgb(var(--rgb))]/10 ',
                            'border border-[rgb(var(--rgb))]',
                            {
                              'pointer-events-none': isCompleted,
                              'shadow-[2px_4px_8px_rgba(var(--rgb)/0.1)]': !isCompleted,
                            },
                          )}
                          style={
                            isCompleted
                              ? {
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  //@ts-ignore
                                  // '--rgb': hexToRgb('#918FA0').join(' '),
                                  '--rgb': hexToRgb('#00a643').join(' '),
                                }
                              : {}
                          }
                        >
                          <span className="line-clamp-4 break-words text-lg font-semibold leading-snug text-[rgb(var(--rgb))]">
                            {quest.attributes.name}
                          </span>

                          <div className="flex gap-2 mt-auto items-end justify-between">
                            <span
                              className={classNames(
                                tagClassName,
                                'border-[rgb(var(--rgb))] bg-[rgb(var(--rgb))]',
                                // 'text-[rgb(var(--rgb))]',
                                'text-neutral-50 mr-auto',
                              )}
                            >
                              {xp} XP
                            </span>

                            <div className="flex gap-2 flex-wrap justify-end">
                              {isCompleted ? (
                                <span
                                  className={classNames(
                                    tagClassName,
                                    'bg-green-100 text-green-800',
                                  )}
                                >
                                  COMPLETED
                                </span>
                              ) : (
                                <>
                                  {!isOneTime && !!count && (
                                    <>
                                      <div className="flex justify-end basis-full">
                                        <span
                                          className={classNames(
                                            tagClassName,
                                            'bg-neutral-100 text-neutral-600 uppercase',
                                          )}
                                        >
                                          {count} COMPLETED
                                        </span>
                                      </div>

                                      <span
                                        className={classNames(
                                          tagClassName,
                                          'bg-neutral-100 text-neutral-600 uppercase',
                                        )}
                                      >
                                        {totalXp} XP Earned
                                      </span>
                                    </>
                                  )}

                                  <span
                                    className={classNames(
                                      tagClassName,
                                      'bg-neutral-100 text-neutral-600 uppercase',
                                    )}
                                  >
                                    {frequency}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </a>
                      </li>
                    );
                  })}

                  {i === 0 && (
                    <Leaderboard className="col-start-[-2] col-end-auto row-start-[-1] row-end-3 max-xl:hidden border border-[rgb(var(--rgb))]" />
                  )}
                </ul>
              </section>
            );
          })}
        </div>

        <Leaderboard
          className="mt-5 sm:mt-9 max-h-[13.375rem] xl:hidden border border-[rgb(var(--rgb))]"
          style={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            '--rgb': hexToRgb(data?.categories[0]?.attributes.color || '#4056F7').join(' '),
          }}
        />
      </main>

      <Modal {...connectZealyAccountModal.props}>
        <ModalContent className="flex w-full max-w-[29.5rem] text-center flex-col gap-4 p-6 sm:px-12 border-0">
          <ModalCloseButton className="self-end !m-0" />
          <h2 className="text-modal-title">Connect Your Zealy Account!</h2>

          <p className="text-neutral-500">
            In order to participate your wallet must be connected to your zealy account.
          </p>

          <p className="text-neutral-500">
            Ensure you use the same wallet on Zealy and the Magic Store.
          </p>

          <p className="font-medium text-secondary">
            Once achieved, click "Verify" to become eligible to participate.
          </p>

          <a
            href={data?.campaign?.attributes.invite}
            target="_blank"
            rel="noreferrer"
            className="button button-solid-secondary button-lg w-full mt-2"
          >
            Go To Zealy
          </a>
        </ModalContent>
      </Modal>
    </>
  );
};

const BasicStory = ({ story }: { story: CommonStory }) => {
  const { isAuth } = useAuth();
  const authModal = useAuthModal();
  const { title, storyBlocks, layout } = story?.content ?? {};

  const handleClick = (typeFromProps?: string, event?: MouseEvent) => {
    if (!isAuth) {
      authModal.open();

      event?.preventDefault();

      return;
    }
  };

  return (
    <div
      className={classNames('stories-container', layout && `--layout-${layout}`)}
      id="story-link-anchor"
    >
      <StoryHeadBanner story={story} storyTitle={title} />

      <div className="stories-body">
        <StoryMainInfoBlock story={story} onButtonClick={handleClick} />

        <div className="story-list">
          {storyBlocks?.map((block, index) => (
            <StoryBlocksItem
              key={index}
              block={block}
              storyTitle={title}
              onButtonClick={handleClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const HtmlStory = ({ story }: { story: CommonStory }) => {
  const { html, hardcodedHtml } = story?.content ?? {};
  const { isAuth } = useAuth();
  const authModal = useAuthModal();

  const handleHtmlContentClick: MouseEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLAnchorElement;

    if (target?.tagName === 'A') {
      if (target.dataset.authRequired !== 'false' && !isAuth) {
        authModal.open();

        event.preventDefault();

        return;
      }

      target.target = '_blank';
    }
  };

  const rawHtml = useMemo(() => {
    if (hardcodedHtml) {
      return hardcodedHtml;
    }

    if (!html) {
      return '';
    }

    return html.replace(/(?<=(?:src|srcset)=("|'))(.+?)(?=\1)/g, (match) => {
      return generateUrl({
        url: match,
        resizeType: 'fill',
        quality: 90,
        format: 'webp',
      });
    });
  }, [hardcodedHtml, html]);

  return (
    <div className="story-page flex-auto">
      <div
        className="story-page__content"
        onClick={handleHtmlContentClick}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(rawHtml),
        }}
      />
    </div>
  );
};

const StoryPage = () => {
  const { storyId } = useParams() as { storyId: string };
  const { data: storyData } = useStoryQuery(storyId, hardcodedStories);

  if (!storyData) {
    notFound();
  }

  if (storyData.type === 'ZEALY') {
    return <ZealyStory />;
  }

  if (storyData.type === 'ZEALY_LEADERBOARD') {
    return <ZealyLeaderboardStory />;
  }

  if (storyData.type === 'STORM_TRADE') {
    return <StormTradeStory />;
  }

  const { type } = storyData?.content ?? {};

  if (type === ACTION_TYPES.SOQUEST) {
    return <SoquestStory story={storyData} />;
  }

  if (type === ACTION_TYPES.HTML) {
    return <HtmlStory story={storyData} />;
  }

  return <BasicStory story={storyData} />;
};

export default StoryPage;
